import { useQueryClient } from "@tanstack/react-query";
import {
  FileUpload,
  Sidebar,
  Select,
  TextArea,
  BaseButton,
} from "@velocity-ehs/component-library";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import EhsConfigService from "../../../services/EhsConfigService";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import classes from "./ConfigFileSliders.module.scss";
import { find } from "lodash";
import { useEffect } from "react";
import { LANGUAGE_PATH } from "./NavHelpers";

type UploadFileFormFields = {
  file: File;
  configType: {
    id: string;
  };
  description: string;
  statusOnUpload: { value: boolean };
};

interface StatusOption {
  label: string;
  value: boolean;
}

interface UploadConfigFileSliderProps {
  showSlider: boolean;
  currentId: string;
  configTypes: any[];
  languageConfigTypes: any[];
  closeSliderCallback?: () => void;
}

const UploadConfigFileSlider: React.FC<UploadConfigFileSliderProps> = ({
  showSlider,
  currentId,
  configTypes,
  languageConfigTypes,
  closeSliderCallback,
}) => {
  const isLanguageConfig = currentId === LANGUAGE_PATH;
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarActionContext();

  const statusOptions: StatusOption[] = [
    { label: t("status_active"), value: true },
    { label: t("status_inactive"), value: false },
  ];

  const formMethods = useForm({
    mode: "onBlur",
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
  } = formMethods;

  useEffect(() => {
    setValue(
      "configType",
      find(configTypes, (config) => {
        return config.name === currentId;
      })
    );
    setValue("statusOnUpload", statusOptions[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, configTypes.length]);

  const queryClient = useQueryClient();

  const onSubmit = async (values: UploadFileFormFields) => {
    const { file, configType, description, statusOnUpload } = values;

    try {
      await EhsConfigService.uploadConfigFile(
        file,
        UsersService.customer.id,
        configType.id,
        description,
        statusOnUpload.value
      );

      queryClient.invalidateQueries({ queryKey: ["ehsConfigurations"] });

      onClose();
      showSnackbar(SnackbarType.Success, t("upload_configuration_success"));
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  const onClose = () => {
    reset();
    closeSliderCallback();
  };

  const footerButtons = () => {
    return (
      <>
        <BaseButton
          type="submit"
          label={t("universal_Upload")}
          loading={isSubmitting}
          form="ehsConfigurations"
          className={classes.saveButton}
        />
        <BaseButton
          label={t("universal_Cancel")}
          className="p-button-text"
          onClick={onClose}
        />
      </>
    );
  };

  return (
    <Sidebar
      header={t("upload_configuration_title")}
      visible={showSlider}
      onHide={onClose}
      footer={footerButtons()}
    >
      <form id="ehsConfigurations" onSubmit={handleSubmit(onSubmit)}>
        <FileUpload
          formMethods={formMethods}
          rules={{
            required: t("upload_configuration_file_required"),
          }}
        />
        <Select
          control={control}
          name="configType"
          label={t("upload_configuration_type_label")}
          options={isLanguageConfig ? languageConfigTypes : configTypes}
          readOnly={!isLanguageConfig}
          display="displayName"
          required
          errorMessage={errors ? errors.configType?.message : undefined}
        />
        <Select
          control={control}
          name="statusOnUpload"
          label={t("upload_configuration_status_label")}
          options={statusOptions}
          required
          errorMessage={errors ? errors.statusOnUpload?.message : undefined}
        />
        <TextArea
          name="description"
          control={control}
          label={t("universal_Description")}
        />
      </form>
    </Sidebar>
  );
};

export { UploadConfigFileSlider };
